import ApplicationController from './application_controller'

export default class extends ApplicationController {
  static values = {
    index: { type: Number, default: 0},
    enterguard: Boolean,
    inloopId: String,
    engagementId: String,
    brainDigitalGardenId: String,
    llmsDigitalGardenId: String,
    contextMode: String,
  }
  static targets = ['list','autocompletion','textarea','emojipicker','contextbar']

  connect() {
    super.connect()
    this.boundPleaseWaitMethod = this.handlePleaseWait.bind(this)
    document.addEventListener('pleaseWait', this.boundPleaseWaitMethod)

    const storeSignedIds = localStorage.getItem(this.storageKeyForSignedIds())

    this.uploadedImageSignedIds = storeSignedIds || []

    // Preserve original autocompletion list (for "/" commands)
    try { this._originalListHTML = this.listTarget?.innerHTML } catch(e) { /* noop */ }
    this._mentionListRendered = false
    this._mentionData = null

    // Load contexts in overlay (kiosk) if bar exists and auto-add defaults if empty
     if (this.hasContextbarTarget) {
       this.loadContexts().then(count => {
         if (count === 0) {
           const ops = []
           const mode = this.hasContextModeValue && this.contextModeValue ? this.contextModeValue : 'inloop'

          if (mode === 'digital') {
            const digitalGardenIds = []
            if (this.hasBrainDigitalGardenIdValue && this.brainDigitalGardenIdValue) digitalGardenIds.push(this.brainDigitalGardenIdValue)
            if (this.hasLlmsDigitalGardenIdValue && this.llmsDigitalGardenIdValue) digitalGardenIds.push(this.llmsDigitalGardenIdValue)

            Array.from(new Set(digitalGardenIds)).forEach(id => {
              ops.push(this.addDigitalGardenContext(id))
            })

            if (digitalGardenIds.length === 0 && this.hasInloopIdValue && this.inloopIdValue) {
              ops.push(this.addInloopContext())
            }
          } else if (mode === 'engagement') {
            if (this.hasEngagementIdValue && this.engagementIdValue) {
              ops.push(this.addEngagementContext())
            } else if (this.hasInloopIdValue && this.inloopIdValue) {
              ops.push(this.addInloopContext())
            }
          } else if (mode === 'analytic') {
            if (this.hasEngagementIdValue && this.engagementIdValue) ops.push(this.addEngagementContext())
            const analyticDigitalGardenIds = []
            if (this.hasBrainDigitalGardenIdValue && this.brainDigitalGardenIdValue) analyticDigitalGardenIds.push(this.brainDigitalGardenIdValue)
            if (this.hasLlmsDigitalGardenIdValue && this.llmsDigitalGardenIdValue) analyticDigitalGardenIds.push(this.llmsDigitalGardenIdValue)
            Array.from(new Set(analyticDigitalGardenIds)).forEach(id => {
              ops.push(this.addDigitalGardenContext(id))
            })
            if (ops.length === 0 && this.hasInloopIdValue && this.inloopIdValue) ops.push(this.addInloopContext())
          } else if (this.hasInloopIdValue && this.inloopIdValue) {
            ops.push(this.addInloopContext())
          }

           if (ops.length > 0) {
             Promise.allSettled(ops).then(() => this.loadContexts().catch(()=>{}))
           }
         }
     }).catch(()=>{})
     }
  }
 
  async addInloopContext() {
    if (!this.hasInloopIdValue || !this.inloopIdValue) return
    await this._postJSON(`/conversations/${this.element.dataset.id}/add_inloop_context`, { inloop_id: this.inloopIdValue })
    this._toast('Inloop context added')
  }

  async addEngagementContext() {
    if (!this.hasEngagementIdValue || !this.engagementIdValue) return
    await this._postJSON(`/conversations/${this.element.dataset.id}/add_engagement_context`, { engagement_id: this.engagementIdValue })
    this._toast('Engagement context added')
  }

  async addInloopDeliverableContext() {
    if (!this.hasInloopIdValue || !this.inloopIdValue) return
    await this._postJSON(`/conversations/${this.element.dataset.id}/add_inloop_deliverable_context`, { inloop_id: this.inloopIdValue })
    this._toast('Inloop deliverable added')
  }

  // Removed: engagement deliverable is included in engagement context

  async addEngagementContextById(engagementId) {
    if (!engagementId) return
    await this._postJSON(`/conversations/${this.element.dataset.id}/add_engagement_context`, { engagement_id: engagementId })
    this._toast('Engagement context added')
  }

  // Removed: engagement deliverable is now included within engagement context

  async addInloopContextById(inloopId) {
    if (!inloopId) return
    await this._postJSON(`/conversations/${this.element.dataset.id}/add_inloop_context`, { inloop_id: inloopId })
    this._toast('Inloop context added')
  }

  async addDigitalGardenContext(digitalGardenId = null) {
    const resolvedId = digitalGardenId
    if (!resolvedId) return

    await this._postJSON(`/conversations/${this.element.dataset.id}/add_digital_garden_context`, { digital_garden_id: resolvedId })

    this._toast('Digital garden context added')
  }

  async addConversationContextById(conversationId) {
    if (!conversationId) return
    await this._postJSON(`/conversations/${this.element.dataset.id}/add_conversation_context`, { context_conversation_id: conversationId })
    this._toast('Conversation context added')
  }

  async loadContexts() {
    const data = await this._getJSON(`/conversations/${this.element.dataset.id}/contexts`)
    if (!this.hasContextbarTarget) return 0
    const items = (data || []).map(c => {
      const badge = document.createElement('span')
      badge.className = 'inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-slate-200 text-slate-700'
      badge.innerHTML = `<strong class=\"uppercase\">${c.kind}</strong> <span class=\"truncate max-w-[14rem]\">${this._escape(c.title || '')}</span>`
      const x = document.createElement('button')
      x.type = 'button'
      x.className = 'ml-1 text-slate-500 hover:text-slate-700'
      x.innerHTML = '&times;'
      x.addEventListener('click', async () => { await this._delete(`/conversations/${this.element.dataset.id}/remove_context/${c.id}`); this.loadContexts() })
      badge.appendChild(x)
      return badge
    })
    this.contextbarTarget.innerHTML = ''
    items.forEach(el => this.contextbarTarget.appendChild(el))
    return items.length
  }

  async _postJSON(url, body) {
    const tokenEl = document.querySelector('meta[name="csrf-token"]')
    const csrf = tokenEl ? tokenEl.getAttribute('content') : undefined
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(csrf ? { 'X-CSRF-Token': csrf } : {})
      },
      body: JSON.stringify(body || {})
    })
    if (!res.ok) {
      const msg = await res.text()
      throw new Error(msg || 'Request failed')
    }
    // refresh bar if present
    if (this.hasContextbarTarget) this.loadContexts().catch(()=>{})
    return res.json().catch(() => ({}))
  }

  async _getJSON(url) {
    const res = await fetch(url, { headers: { 'Accept': 'application/json' } })
    if (!res.ok) return []
    return res.json().catch(() => [])
  }

  async _delete(url) {
    const tokenEl = document.querySelector('meta[name="csrf-token"]')
    const csrf = tokenEl ? tokenEl.getAttribute('content') : undefined
    const res = await fetch(url, { method: 'DELETE', headers: { ...(csrf ? { 'X-CSRF-Token': csrf } : {}) } })
    if (!res.ok) return false
    return true
  }

  _toast(message) {
    try {
      const div = document.createElement('div')
      div.className = 'fixed bottom-16 right-6 bg-slate-800 text-white text-sm px-3 py-2 rounded shadow-lg z-50'
      div.textContent = message
      document.body.appendChild(div)
      setTimeout(() => div.remove(), 1500)
    } catch (e) { /* noop */ }
  }

   handleUploaded(event) {
    this.uploadedImageSignedIds = event.detail.signedIds

    if (event.detail.storeSignedIds) {
      localStorage.setItem(this.storageKeyForSignedIds(), this.uploadedImageSignedIds)
    }
  }

  handlePleaseWait(event) {
    console.log("handlePleaseWait(event)", event, event.detail, this.element.dataset.id, this.textareaTarget)
    if(event.detail.conversationId == this.element.dataset.id) {
      alert("Please wait for assistant to finish typing its response.")
      this.textareaTarget.innerText = event.detail.message
    }
  }

  input() {
    this.showOrHideList();

    // Swap list to mention suggestions when starting with '@'
    const text = (this.textareaTarget.innerText || '').trim()
    if (text.startsWith('@')) {
      this.ensureMentionList().catch(()=>{})
    } else if (text.startsWith('/')) {
      this.restoreOriginalList()
    } else {
      // Hide if not a command prefix
      this.restoreOriginalList(false)
    }

    this.filterAutocompletions()
    this.revealFilteredAutocompletions()
    this.resetIndex()
    this.highlightIndex()
  }

  showOrHideList() {
    const text = this.textareaTarget.innerText
    if (text && (text[0] === '/' || text[0] === '@')) {
      this.listTarget.classList.remove('hidden')
    } else {
      this.listTarget.classList.add('hidden')
    }
  }

  filterAutocompletions() {
    const q = (this.textareaTarget.innerText || '').toLowerCase()
    this.filteredAutocompletions = this.autocompletionTargets.filter(e => (e.dataset.name || '').toLowerCase().startsWith(q))
  }

  revealFilteredAutocompletions() {
    this.autocompletionTargets.forEach(e => e.classList.add('hidden'))
    this.filteredAutocompletions.forEach(e => e.classList.remove('hidden'))
  }

  highlightIndex() {
    this.autocompletionTargets.forEach(e => e.classList.remove('bg-slate-400', 'text-slate-700'))
    this.autocompletionTargets.forEach(e => e.classList.add('text-slate-500'))
    if (this.filteredAutocompletions[this.index]) {
      this.filteredAutocompletions[this.index].classList.remove('text-slate-500')
      this.filteredAutocompletions[this.index].classList.add('bg-slate-400', 'text-slate-700')
    }
  }

  resetIndex() {
    this.filteredAutocompletions.length < this.autocompletionTargets.length ? this.index = 0 : this.index = undefined
  }

   keydown(event) {
     const textNow = this.textareaTarget.innerText
     const isSlashCommand = /^\/\w*$/.test(textNow)
     const isMentionCommand = /^@\S*$/.test(textNow)
     const isEnter = event.key === 'Enter'
     const isSpace = event.key === ' '
     const isShift = event.shiftKey
     const isModifiedEnter = (event.metaKey && isEnter) || (event.ctrlKey && isEnter) // work for both mac and windows

    // Lightweight "@" mentions for context
    const atInloop = /@inloop\s?$/.test(textNow)
    const atEng = /@eng(agement)?\s?$/.test(textNow)
    const atBrain = /@brain\s?$/.test(textNow)
    const atLlms = /@llms\s?$/.test(textNow)
    if ((isSpace || isEnter) && (atInloop || atEng || atBrain || atLlms)) {
      event.preventDefault()
      if (atInloop) { this.addInloopContext().catch(()=>{}) }
      if (atEng) { this.addEngagementContext().catch(()=>{}) }
      if (atBrain && this.hasBrainDigitalGardenIdValue && this.brainDigitalGardenIdValue) { this.addDigitalGardenContext(this.brainDigitalGardenIdValue).catch(()=>{}) }
      if (atLlms && this.hasLlmsDigitalGardenIdValue && this.llmsDigitalGardenIdValue) { this.addDigitalGardenContext(this.llmsDigitalGardenIdValue).catch(()=>{}) }
      // remove the mention text
      this.textareaTarget.innerText = textNow.replace(/@(inloop|eng(?:agement)?|brain|llms)\s?$/, '')
      return
    }


    // let shift+enter just be itself, no matter what
    if (isShift && isEnter) return

    // hit enter to submit highlighted slash or mention command (don't require command/enter in EnterGuard mode)
    if (isEnter && (isSlashCommand || isMentionCommand)) return this.submitHighlightedSelection(event)

    // hit enter to submit while not in EnterGuard mode
    if (isEnter && !isSlashCommand && !this.enterguardValue) return this.prompt(event)

    // hit modified-enter to submit prompt in EnterGuard mode
    if (isModifiedEnter && this.enterguardValue) return this.prompt(event)

    if (event.key === 'Tab' && (isSlashCommand || isMentionCommand)) return this.completeHighlightedSelection()
    if (event.key === 'ArrowRight' && (isSlashCommand || isMentionCommand)) return this.completeHighlightedSelection()
    if (event.key === 'ArrowUp' && (isSlashCommand || isMentionCommand)) return this.decrementIndex()
    if (event.key === 'ArrowDown' && (isSlashCommand || isMentionCommand)) return this.incrementIndex()
    if (event.key === 'Escape') return this.textareaTarget.blur()
  }

  decrementIndex() {
    if (this.index === undefined) this.index = this.filteredAutocompletions.length
    this.index = Math.max(0, this.index - 1)
    this.highlightIndex()
  }

  incrementIndex() {
    if (this.index === undefined) this.index = -1
    this.index = Math.min(this.filteredAutocompletions.length - 1, this.index + 1)
    this.highlightIndex()
  }

  completeHighlightedSelection() {
    const autocompletion = this.filteredAutocompletions[this.index]
    if (!autocompletion) return

    const type = autocompletion.dataset.type
    if (type === 'inloop') {
      const id = autocompletion.dataset.id
      ;(id ? this.addInloopContextById(id) : this.addInloopContext()).catch(()=>{})
      this.textareaTarget.innerText = ''
    } else if (type === 'engagement') {
      const id = autocompletion.dataset.id
      this.addEngagementContextById(id).catch(()=>{})
      this.textareaTarget.innerText = ''
    } else if (type === 'conversation') {
      const id = autocompletion.dataset.id
      this.addConversationContextById(id).catch(()=>{})
      this.textareaTarget.innerText = ''
    } else {
      this.textareaTarget.innerText = autocompletion.dataset.name
    }
    this.blur()
  }

  submitHighlightedSelection(event) {
    const autocompletion = this.filteredAutocompletions[this.index]
    if (!autocompletion) return

    const name = autocompletion.dataset.name || ''
    const type = autocompletion.dataset.type
    if (name.startsWith('@') || type) {
      // Handle @ mentions as context add or insertion
      if (type === 'inloop') {
        const id = autocompletion.dataset.id
        ;(id ? this.addInloopContextById(id) : this.addInloopContext()).catch(()=>{})
      } else if (type === 'engagement') {
        const id = autocompletion.dataset.id
        this.addEngagementContextById(id).catch(()=>{})
      } else if (type === 'conversation') {
        const id = autocompletion.dataset.id
        this.addConversationContextById(id).catch(()=>{})
      } else if (name === '@inloop') {
        this.addInloopContext().catch(()=>{})
      } else if (name === '@engagement') {
        this.addEngagementContext().catch(()=>{})
      } else if (name === '@brain' && this.hasBrainDigitalGardenIdValue && this.brainDigitalGardenIdValue) {
        this.addDigitalGardenContext(this.brainDigitalGardenIdValue).catch(()=>{})
      } else if (name === '@llms' && this.hasLlmsDigitalGardenIdValue && this.llmsDigitalGardenIdValue) {
        this.addDigitalGardenContext(this.llmsDigitalGardenIdValue).catch(()=>{})
      } else {
        // Fallback: insert name into prompt
        this.textareaTarget.innerText = name
      }
      this.textareaTarget.innerText = ''
      this.blur()
      event.preventDefault()
      return
    }

    this.completeHighlightedSelection()
    this.prompt(event)
  }

  draft(event) {
    const prompt = this.textareaTarget.innerText.trim()
    console.log('saving draft', prompt)
    this.stimulate('ConversationReflex#draft_prompt', prompt)
  }

  prompt(event) {
    event.preventDefault();
    const prompt = this.textareaTarget.innerText.trim()
    if (prompt.length === 0) return; // prevent empty prompts

    if (prompt.length > 20000) { // prevent too long prompts
      alert("Your prompt is too long, please shorten it. (ProPlus members may upload large documents directly.)")
      return;
    }

    console.log('prompting', prompt)

    if (prompt === "/clear" || prompt === "/delete") {
      const command = prompt.split("/")[1]
      if (!window.confirm(this.element.dataset[`confirm${command}`])) {
        this.textareaTarget.innerText = ""
        return
      }

      if (command === "clear") {
        document.querySelector(".messages").innerHTML = ""
        this.textareaTarget.innerText = ""
      } else {
        this.dispatchCStatusEvent(this.element.dataset.deletingMessage)
        this.disable()
      }
    } else {
      this.textareaTarget.innerText = ""
    }

    this.stimulate(
      'ConversationReflex#prompt',
      this.textareaTarget,
      {
        prompt,
        images: this.uploadedImageSignedIds,
      }
    )

    this.uploadedImageSignedIds = []
    this.dispatch("clearUploaded")
  }

  clickOption(event) {
    const el = event.currentTarget
    const name = el.dataset.name || ''
    const type = el.dataset.type
    if (type === 'inloop') {
      const id = el.dataset.id
      ;(id ? this.addInloopContextById(id) : this.addInloopContext()).catch(()=>{})
      this.textareaTarget.innerText = ''
      this.blur()
      return
    }
    if (type === 'engagement') {
      const id = el.dataset.id
      this.addEngagementContextById(id).catch(()=>{})
      this.textareaTarget.innerText = ''
      this.blur()
      return
    }
    if (type === 'deliverable_inloop') {
      const id = el.dataset.id
      if (id && (!this.hasInloopIdValue || !this.inloopIdValue)) {
        this.inloopIdValue = id
      }
      this.addInloopDeliverableContext().catch(()=>{})
      this.textareaTarget.innerText = ''
      this.blur()
      return
    }
    // Removed: deliverable_engagement click handler
    if (type === 'conversation') {
      const id = el.dataset.id
      this.addConversationContextById(id).catch(()=>{})
      this.textareaTarget.innerText = ''
      this.blur()
      return
    }
    if (name.startsWith('@')) {
      if (name === '@inloop') this.addInloopContext().catch(()=>{})
      if (name === '@engagement') this.addEngagementContext().catch(()=>{})
      if (name === '@brain' && this.hasBrainDigitalGardenIdValue && this.brainDigitalGardenIdValue) this.addDigitalGardenContext(this.brainDigitalGardenIdValue).catch(()=>{})
      if (name === '@llms' && this.hasLlmsDigitalGardenIdValue && this.llmsDigitalGardenIdValue) this.addDigitalGardenContext(this.llmsDigitalGardenIdValue).catch(()=>{})
      // Fallback: if it's an @ mention not handled above (e.g., conversation title), insert the name
      if (!['@inloop','@engagement','@brain','@llms'].includes(name)) {
        this.textareaTarget.innerText = name
      } else {
        this.textareaTarget.innerText = ''
      }
      this.blur()
      return
    }

    this.textareaTarget.innerText = name
    this.prompt(event)
    this.blur()
  }

  blur() {
    setTimeout(() => {
      this.listTarget.classList.add('hidden');
    }, 100);
  }

  disable() {
    this.textareaTarget.disabled = true
    this.textareaTarget.contentEditable = false
    this.textareaTarget.classList.add("text-slate-400", "disabled")
  }

  storageKeyForSignedIds() {
    return `prompter(${this.element.dataset.id})#imageSignedIds`
  }

  // --- Mention suggestions helpers ---
  async loadMentionData() {
    if (this._mentionData && Array.isArray(this._mentionData.engagementConversations) && this._mentionData.engagementConversations.length > 0) {
      return this._mentionData
    }
    const data = { inloop: null, engagements: [], conversation: null, engagementConversations: [] }
    try {
      const inloopId = this.hasInloopIdValue ? this.inloopIdValue : null
      if (inloopId) {
        const res = await fetch(`/inloops/${inloopId}.json`, { headers: { 'Accept': 'application/json' }})
        if (res.ok) {
          const json = await res.json()
          data.inloop = { id: json.id, code: json.code }
          data.engagements = (json.engagements || []).map(e => ({ id: e.id, name: e.name }))
        }
      }

      // Current conversation title (informational), skip facilitator chat
      const title = this.textareaTarget?.dataset?.conversationTitle || this.element?.dataset?.conversationTitle
      if (title && !/^facilitator chat \(/i.test(title)) {
        data.conversation = { id: this.element.dataset.id, title }
      }

      // Load conversations from all engagements if available
      const engagementIds = (data.engagements || []).map(e => e.id).filter(Boolean)
      if (inloopId && engagementIds.length > 0) {
        try {
          const lists = await Promise.all(
            engagementIds.map(async (eid) => {
              const r = await fetch(`/inloops/${inloopId}/engagements/${eid}/conversations_list`, { headers: { 'Accept': 'application/json' }})
              if (!r.ok) return { conversations: [] }
              return r.json().catch(() => ({ conversations: [] }))
            })
          )
          const merged = []
          const seen = new Set()
          lists.forEach(j => {
            (j.conversations || []).forEach(c => {
              const t = (c && c.title) ? String(c.title) : ''
              const owner = (c && c.owner_name) ? String(c.owner_name) : ''
              if (!t || /^facilitator chat \(/i.test(t)) return
              if (c && !seen.has(c.id)) { seen.add(c.id); merged.push({ id: c.id, title: t, ownerName: owner }) }
            })
          })
          data.engagementConversations = merged
        } catch (_) { /* noop */ }
      }
    } catch(e) { /* noop */ }
    this._mentionData = data
    return data
  }

  async ensureMentionList() {
    if (!this.listTarget) return
    if (this._mentionListRendered) return
    const data = await this.loadMentionData()
    this.listTarget.innerHTML = this.renderMentionItemsHTML(data)
    this._mentionListRendered = true
    // After swapping list content, recompute and show filtered items
    try {
      this.filterAutocompletions()
      this.revealFilteredAutocompletions()
      this.resetIndex()
      this.highlightIndex()
    } catch (_) { /* noop */ }
  }

  restoreOriginalList(showIfCommand = true) {
    if (!this.listTarget) return
    if (!this._mentionListRendered) return
    this.listTarget.innerHTML = this._originalListHTML || ''
    this._mentionListRendered = false
    if (!showIfCommand) this.listTarget.classList.add('hidden')
  }

  renderMentionItemsHTML(data) {
    const items = []
    // Inloop item
    if (data.inloop) {
      items.push(this.renderMentionItem({
        name: '@inloop',
        label: `Inloop ${this._escape(data.inloop.code || '')}`,
        type: 'inloop',
        id: data.inloop.id
      }))
      items.push(this.renderMentionItem({
        name: '@inloop-deliverable',
        label: `Inloop Deliverable`,
        type: 'deliverable_inloop',
        id: data.inloop.id
      }))
    }
    // Engagements
    (data.engagements || []).forEach(e => {
      items.push(this.renderMentionItem({
        name: `@${e.name || 'engagement'}`,
        label: `Engagement ${this._escape(e.name || 'Engagement')}`,
        type: 'engagement',
        id: e.id
      }))
      // Engagement deliverable item removed — combined with engagement
    })
    // Conversation title (informational; inserts text)
    if (data.conversation && data.conversation.title) {
      items.push(this.renderMentionItem({
        name: `@${data.conversation.title}`,
        label: `Conversation ${this._escape(data.conversation.title)}`,
        type: '',
        id: data.conversation.id
      }))
    }
    // Engagement conversations (add as full transcript context)
    if (Array.isArray(data.engagementConversations)) {
      data.engagementConversations.forEach(c => {
        const title = (c && c.title) ? String(c.title) : ''
        const owner = (c && c.ownerName) ? String(c.ownerName) : ''
        if (!title) return
        let appendOwner = !!owner
        if (appendOwner) {
          const ownerTrim = owner.trim()
          const titleTrim = title.trim()
          const ownerLower = ownerTrim.toLowerCase()
          const titleLower = titleTrim.toLowerCase()
          // If title already ends with (Owner) or equals owner, don't append
          if (titleLower === ownerLower) {
            appendOwner = false
          } else {
            try {
              const escaped = ownerTrim.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
              const re = new RegExp(`\\(\\s*${escaped}\\s*\\)\\s*$`, 'i')
              if (re.test(titleTrim)) appendOwner = false
            } catch (_) { /* noop */ }
          }
        }
        const label = appendOwner ? `Conversation ${this._escape(title)} (${this._escape(owner)})` : `Conversation ${this._escape(title)}`
        // Only offer full transcript by default
        items.push(this.renderMentionItem({
          name: `@${title}`,
          label,
          type: 'conversation',
          id: c.id
        }))
      })
    }
    return items.join('')
  }

  renderMentionItem({ name, label, type, id }) {
    return `
      <div data-prompter-target="autocompletion" class="p-3 hover:text-slate-700 hover:bg-slate-400 text-slate-500" data-name="${this._escape(name)}" data-type="${this._escape(type || '')}" data-id="${this._escape(id || '')}" data-action="click->prompter#clickOption">
        <div><span class="font-medium">${label}</span></div>
      </div>
    `
  }

  iconFor(name) {
    // Minimal inline SVG set; avoids server helpers
    switch(name) {
      case 'chat-bubble-left-right':
        return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4"><path d="M7.5 2.25A2.25 2.25 0 005.25 4.5v3.75A2.25 2.25 0 007.5 10.5h6a2.25 2.25 0 002.25-2.25V4.5A2.25 2.25 0 0013.5 2.25h-6z"/><path d="M6 13.5a2.25 2.25 0 00-2.25 2.25V21l3.75-2.25H16.5A2.25 2.25 0 0018.75 16.5v-3.75A2.25 2.25 0 0016.5 10.5h-7.5"/></svg>'
      case 'chat-bubble-left-ellipsis':
        return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4"><path d="M7.5 2.25A2.25 2.25 0 005.25 4.5v6A2.25 2.25 0 007.5 12.75h6A2.25 2.25 0 0015.75 10.5v-6A2.25 2.25 0 0013.5 2.25h-6z"/><path d="M6 15a2.25 2.25 0 00-2.25 2.25V21l3.75-2.25H16.5A2.25 2.25 0 0018.75 16.5V15"/><circle cx="10.5" cy="7.5" r="1"/><circle cx="13.5" cy="7.5" r="1"/><circle cx="7.5" cy="7.5" r="1"/></svg>'
      case 'document-text':
      default:
        return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4"><path d="M4.5 3.75A1.5 1.5 0 016 2.25h7.5L19.5 7.5v12.75a1.5 1.5 0 01-1.5 1.5H6a1.5 1.5 0 01-1.5-1.5V3.75z"/><path d="M13.5 2.25V6a1.5 1.5 0 001.5 1.5h3.75"/></svg>'
    }
  }

  _escape(value) {
    try {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
    } catch (e) {
      return ''
    }
  }
}
